import { makeAutoObservable, runInAction } from 'mobx'
import { isAxiosError } from 'axios'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { APP_ENV } from 'shared/config'
import { Phone } from 'entities/Phone/model/Phone'
import { PhoneApi } from 'entities/Phone'
import {
  type IParamsCreateNumbersLandline,
  type IResponseCreateNumbersLandlineError,
  type IResponseNumberLandline,
} from 'entities/Phone/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type NumbersLayerStore } from 'pages/settings/pages/numbers/store/numbersLayerStore'
import { NumbersPath } from 'pages/settings/pages/numbers/route/numbersPath'
import { EnterNumberStore } from 'pages/settings/ui/EnterNumberCommon/store/EnterNumberStore'
import { EnterAddressStore } from 'pages/settings/ui/EnterAddressCommon/store/EnterAddressStore'
import { VerifyNumberStore } from 'pages/settings/ui/VerifyNumberCommon/store/VerifyNumberStore'
import { AssignNumberStore } from 'pages/settings/pages/numbers/pages/TextEnableLandline/ui/AssignNumber'
import { InboxesPath } from 'pages/settings/pages/inboxes/route/inboxesPath'
import { AuthorizationDocumentStore } from 'pages/settings/ui/AuthorizationDocumentCommon/store/AuthorizationDocumentStore'

export enum TextEnableLandlineRoutesSteps {
  EnterNumber = 'enter-number',
  EnterAddress = 'enter-address',
  VerifyNumber = 'verify-number',
  AuthorizationDocument = 'authorization-document',
  AssignNumber = 'assign-number',
}

const titleMap: Record<string, string> = {
  [TextEnableLandlineRoutesSteps.EnterNumber]: 'Enter number',
  [TextEnableLandlineRoutesSteps.EnterAddress]: 'Enter address',
  [TextEnableLandlineRoutesSteps.VerifyNumber]: 'Verify number',
  [TextEnableLandlineRoutesSteps.AuthorizationDocument]: 'Authorization Document (LOA)',
  [TextEnableLandlineRoutesSteps.AssignNumber]: 'Assign number',
}

const numberLandlineStatus: IResponseNumberLandline['status'][] = [
  'twilio-processing',
  'received',
  'pending-verification',
  'verified',
  'pending-loa',
  'carrier-processing',
  'testing',
  'completed',
  'action-required',
  'failed',
  'canceled',
]

export class TextEnableLandlineStore {
  private _currentStep = 1

  private readonly _enterNumberStore: EnterNumberStore
  private readonly _enterAddressStore: EnterAddressStore
  private readonly _verifyNumberStore: VerifyNumberStore
  private readonly _authorizationDocumentStore: AuthorizationDocumentStore
  private readonly _assignNumberStore: AssignNumberStore

  private _phoneQuery: string | null = null
  private _phone: Phone | null = null
  private _cancelRequestLandline = new RequestCancelAxios()
  private _loading = true

  constructor(private _numbersLayerStore: NumbersLayerStore, private _entities: IEntitiesStore) {
    this._enterNumberStore = new EnterNumberStore({
      cancelRequestLandline: this._cancelRequestLandline,
      fetchNumbersLandline: this.fetchNumbersLandline,
    })
    this._enterAddressStore = new EnterAddressStore({
      fetchNumbersLandline: this.fetchNumbersLandline,
      createNumbersLandline: this.createNumbersLandline,
      getPhoneQuery: () => this.phoneQuery,
      nextStepByNumber: this.nextStepByNumber,
      nextStepNumber: 3,
    })
    this._verifyNumberStore = new VerifyNumberStore(
      {
        fetchNumbersLandline: this.fetchNumbersLandline,
        getPhone: () => this.phone,
        getPayload: () => this.payload,
        setPhone: this.setPhone,
        syncData: this.syncData,
        initStep: this.initStep,
      },
      this._entities
    )
    this._authorizationDocumentStore = new AuthorizationDocumentStore(
      {
        getPayload: () => this.payload,
        fetchNumbersLandline: this.fetchNumbersLandline,
        syncData: this.syncData,
        initStep: this.initStep,
        enterNumberStore: this._enterNumberStore,
      },
      this._entities
    )
    this._assignNumberStore = new AssignNumberStore(this, this._entities)

    makeAutoObservable(this)
  }

  get hideBack() {
    if (this._currentStep === 3) return true

    return false
  }

  get loading() {
    return this._loading
  }

  get cancelRequestLandline() {
    return this._cancelRequestLandline
  }

  get phone() {
    return this._phone
  }

  get authorizationDocumentStore() {
    return this._authorizationDocumentStore
  }

  get assignNumberStore() {
    return this._assignNumberStore
  }

  get verifyNumberStore() {
    return this._verifyNumberStore
  }

  get enterNumberStore() {
    return this._enterNumberStore
  }

  get enterAddressStore() {
    return this._enterAddressStore
  }

  get numbersLayerStore() {
    return this._numbersLayerStore
  }

  get rootPath() {
    return NumbersPath.textEnableLandline.steps
  }

  get stepMap() {
    return {
      1: TextEnableLandlineRoutesSteps.EnterNumber,
      2: TextEnableLandlineRoutesSteps.EnterAddress,
      3: TextEnableLandlineRoutesSteps.VerifyNumber,
      4: TextEnableLandlineRoutesSteps.AuthorizationDocument,
      5: TextEnableLandlineRoutesSteps.AssignNumber,
    }
  }

  get getCurrentStep() {
    return this.getStep(0)
  }

  get getNextStep() {
    const step = this.getStep(1)
    if (!step) return this.getCurrentStep
    return step
  }

  get getLastStep() {
    const step = this.getStep(-1)
    if (!step) return this.getCurrentStep
    return step
  }

  get getCurrentUrl() {
    return `${this.rootPath}/${this.getCurrentStep}`
  }

  get getNextUrl() {
    return `${this.rootPath}/${this.getNextStep}`
  }

  get getLastUrl() {
    return `${this.rootPath}/${this.getLastStep}`
  }

  get allStepsLength() {
    return Object.keys(this.stepMap).length
  }

  get payload(): IParamsCreateNumbersLandline {
    return {
      ...this._enterNumberStore.payload,
      ...this._enterAddressStore.payload,
      ...this._verifyNumberStore.payload,
      ...this._assignNumberStore.payload,
    }
  }

  get phoneQuery() {
    return this._phoneQuery
  }

  setPhoneQuery = (value: string) => {
    this._phoneQuery = value
  }

  init = async (query: string) => {
    const urlSearchParams = new URLSearchParams(query)
    this._phoneQuery = urlSearchParams.get('phone')
    const statusQuery =
      APP_ENV === 'production'
        ? null
        : (urlSearchParams.get('status') as IResponseNumberLandline['status'] | null) // TODO: for testing

    if (this._phoneQuery) {
      this._enterNumberStore.setPhoneDisabled(true)
      this._enterNumberStore.setPhone(this._phoneQuery, false)

      const phoneInternational = this._enterNumberStore.phoneInternational
      const data = await this.fetchNumbersLandline(phoneInternational)

      if ('error' in data) {
        if (data.error === 'No such number') {
          uiStore.changeRouteViaVue({
            path: `/${NumbersPath.textEnableLandline.root}`,
          })
        }

        return
      } else if (numberLandlineStatus.includes(statusQuery)) {
        // TODO: For testing
        data.landline?.updateStatus(statusQuery)
      }

      if (this._phone) {
        const landline = this._phone.landline

        if (!landline || landline?.isCompleted) {
          uiStore.changeRouteViaVue({
            path: `/${InboxesPath.root}`,
          })

          return
        }

        this.syncData(this._phone)
        this.initStep(this._phone)
      }
    }

    runInAction(() => {
      this._loading = false
    })
  }

  syncData = (phone: Phone) => {
    this._phone = phone
    this._enterNumberStore.syncData(phone)
    this._enterAddressStore.syncData(phone)
    this._verifyNumberStore.syncData(phone)
  }

  initStep = (phone: Phone) => {
    const landline = phone?.landline

    if (!landline) return

    if (landline.statusTwilioProcessing) {
      this.nextStepByNumber(2)

      return
    }

    if (landline.hasVerify) {
      this.nextStepByNumber(3)

      return
    }

    if (landline.hasVerified || landline.hasLOA) {
      this.nextStepByNumber(4)
      this._authorizationDocumentStore.sync()

      return
    }

    if (landline.hasCompleted) {
      this.nextStepByNumber(5)

      return
    }
  }

  fetchNumbersLandline = async (number: string, update = true) => {
    try {
      this._cancelRequestLandline.init()

      const response = await PhoneApi.getNumbersLandline(
        {
          phone: number,
        },
        {
          cancelToken: this._cancelRequestLandline.token,
        }
      )

      const data = response.data

      if ('error' in data) {
        return {
          error: data.error,
        }
      } else {
        const phone = new Phone(data, this._entities)

        if (update) return this.setPhone(phone)

        return phone
      }
    } catch (e) {
      logger.error(e)

      return {
        error: 'unknow',
      }
    }
  }

  createNumbersLandline = async () => {
    try {
      const response = await PhoneApi.createNumbersLandline(this.payload)
      const data = response.data

      if ('error' in data) {
        return {
          error: data.error,
        }
      }

      const phone = new Phone(data, this._entities)

      this.setPhone(phone)
      this.setPhoneQuery(phone.national_number)

      return phone
    } catch (e) {
      const error = e as Error

      logger.error(error)

      if (isAxiosError<IResponseCreateNumbersLandlineError>(error)) {
        if (!error.response) {
          return {
            error: error.message,
          }
        }

        return {
          error: {
            ...error.response.data,
          },
        }
      }

      return {
        error: error.message,
      }
    }
  }

  dispose = () => {
    this._enterNumberStore.dispose()
    this._phone = null
    this._cancelRequestLandline.reset()
    this._enterAddressStore.dispose()
  }

  setPhone = (phone: Phone) => {
    this._phone = phone

    return this._phone
  }

  setCurrentStep = (step: number) => {
    this._currentStep = step
  }

  getStep = (direction: -1 | 1 | 0) => {
    const allSteps = Object.values(this.stepMap)
    const currentPageIndex = this._currentStep - 1

    return allSteps[currentPageIndex + direction]
  }

  getStepByPath = (stepPath: TextEnableLandlineRoutesSteps) => {
    const allPaths = Object.entries(this.stepMap)
    const stepIndex = allPaths.findIndex((path) => {
      return path[1] === stepPath
    })

    return stepIndex !== -1 ? stepIndex + 1 : 1
  }

  getStepUrlMap = (stepPath: TextEnableLandlineRoutesSteps) => {
    return `${this.rootPath}/${stepPath}`
  }

  nextStepByNumber = (number: number, query?: string) => {
    const step = this.stepMap[number as keyof typeof this.stepMap]

    uiStore.changeRoute({
      path: this.getStepUrlMap(step),
      query: query || uiStore.location?.search,
    })
  }

  decreaseStep = () => {
    uiStore.changeRoute({
      path: this.getLastUrl,
      query: uiStore.location?.search,
    })
  }

  nextClickHandler = () => {
    uiStore.changeRoute({
      path: this.getNextUrl,
      query: uiStore.location?.search,
    })
  }

  getSettingsStep = (path: TextEnableLandlineRoutesSteps) => {
    return this.getStepByPath(path)
  }

  getProgressInfoTitle = (pathname: string) => {
    return titleMap[pathname] || ''
  }
}
